/* Keeping the futures account current without anybody pressing anything.
 *
 * What it may write, and what it may not, is decided in `state/mexcAccount.ts`
 * — this is only the clock. Two things about the clock matter:
 *
 *   - **It is slower than the price cadence.** Marks come from a cached, public
 *     board and cost nothing to ask for often; this is a signed request against
 *     a private endpoint, and asking every minute for a balance that moves when
 *     you trade is a way to spend a rate limit on nothing.
 *   - **It stops when the app is not being looked at.** The same rule the mark
 *     refresh follows, for the same reason: a phone in a pocket has nobody to
 *     show a fresher number to, and the first thing that happens on coming back
 *     is a fetch.
 *
 * A failure is recorded rather than announced. A key that has passed its ninety
 * days should not put a toast on screen every five minutes — but it must not be
 * silent either, or the book quietly stops being current and looks fine. It is
 * written to settings and shown on the broker screen. */

import { useEffect, useRef } from 'react';
import { canReachBrokers } from '../lib/brokerLink';
import { connectionFor, missingFields, brokerById } from '../lib/brokers';
import { MexcRefused, fetchAssets, fetchOpenPositions } from '../lib/mexc';
import { fetchSpotBalances, fetchSpotOpenOrders, fetchSpotPrices, type SpotOrder } from '../lib/mexcSpot';
import { SPOT_ACCOUNT, spotPlan } from '../state/mexcSpot';
import { autoPlan, reconcile } from '../state/mexcAccount';
import { outflowBetween } from '../state/exchangeOutflow';
import { useStore } from '../state/store';

/** Every five minutes at most. See the note above on why not every minute. */
const EVERY_MS = 5 * 60_000;

export function MexcAutoSync() {
  const api = useStore();
  const { state } = api;
  const running = useRef(false);
  const lastTried = useRef(0);

  const def = brokerById('mexc');
  const values = connectionFor(state.settings, 'mexc');
  const ready = def !== undefined
    && missingFields(def, values).length === 0
    && state.settings.mexcAuto !== false;

  /* The book as it stands now, not as it stood when the timer was armed.
   *
   * A timer that closes over the book reconciles every tick against whatever
   * was there when it started — so the first write would be replayed for ever,
   * and a contract opened since would read as one the exchange has never heard
   * of. The refresh scheduler learned this the hard way with quotes; the same
   * rule applies to anything a long-lived timer reads. */
  const latest = useRef(api);
  latest.current = api;

  useEffect(() => {
    if (!ready || !state.ready) return;

    const tick = async () => {
      const { state: now, putPerp, setCash, saveSettings } = latest.current;
      const values = connectionFor(now.settings, 'mexc');
      const account = now.settings.brokerAccounts?.['mexc']
        ?? now.data.accounts.find((a) => a.isFutures)?.id;
      if (!account) return;
      if (running.current || document.visibilityState !== 'visible') return;
      if (!canReachBrokers()) return;   // a browser tab cannot, and says so elsewhere
      if (Date.now() - lastTried.current < EVERY_MS) return;
      lastTried.current = Date.now();
      running.current = true;

      /* The spot wallet, read after the futures one and on its own: a key that
         may read one and not the other, or a spot API having a bad minute,
         leaves the other half as it was. See `state/mexcSpot.ts` for what it
         writes and why it removes nothing. */
      const readSpot = async () => {
        const { state: book, putAccount, putHolding, setCash, saveSettings: save } = latest.current;
        const keys = { apiKey: values['apiKey']!.trim(), secret: values['secret']!.trim() };
        try {
          const [balances, prices] = await Promise.all([fetchSpotBalances(keys), fetchSpotPrices()]);
          const plan = spotPlan(balances, prices, book.data, new Date().toISOString());
          if (plan.newAccount) await putAccount(plan.newAccount);
          const held = book.data.cash.find((c) => c.account === SPOT_ACCOUNT);
          if (!held || Math.abs(held.amount - plan.cashUsd) >= 0.01) {
            await setCash(SPOT_ACCOUNT, plan.cashUsd, 'USD');
          }
          for (const h of plan.holdings) {
            const before = book.data.holdings.find((x) => x.id === h.id);
            if (!before || Math.abs(before.shares - h.shares) > 1e-12) await putHolding(h);
          }
          const orders: SpotOrder[] = [];
          for (const pair of plan.pairs.slice(0, 12)) {
            try { orders.push(...await fetchSpotOpenOrders(keys, pair)); } catch { /* one pair is not the wallet */ }
          }
          await save({
            mexcSpotSyncedAt: new Date().toISOString(),
            mexcSpotError: '',
            mexcSpotLocked: plan.lockedCashUsd,
            mexcSpotOrders: orders,
            mexcSpotGone: plan.gone,
            mexcSpotDust: plan.dust,
          });
        } catch (e) {
          await save({
            mexcSpotError: e instanceof MexcRefused
              ? `MEXC refused it (${e.code}): ${e.message}`
              : e instanceof Error ? e.message : 'unknown error',
          });
        }
      };

      try {
        const keys = { apiKey: values['apiKey']!.trim(), secret: values['secret']!.trim() };
        const assets = await fetchAssets(keys);
        const positions = await fetchOpenPositions(keys);
        const snap = reconcile(assets, positions, now.data.perps, new Date().toISOString());
        const held = now.data.cash.find((c) => c.account === account);
        const plan = autoPlan(snap, held?.amount);

        for (const p of plan.perps) await putPerp(p);
        if (plan.cash !== null) {
          await setCash(account, plan.cash, held?.currency ?? 'USD');
        }
        /* What the exchange has stopped reporting is written down rather than
           acted on. Nothing here closes a contract — see `autoPlan` for why —
           but the screen showing that contract has to be able to say the
           exchange no longer has it, or the book goes on displaying a position
           that ended while every other figure beside it reconciles. */
        /* Money that left the exchange, noticed rather than diagnosed.
           *
           * The free balance is written straight over, so a withdrawal arrives
           * as a smaller number with nothing to say why — and the book's total
           * drops, drawing a fall on the value chart that is indistinguishable
           * from a losing week. What it needs is a record only the person who
           * made the transfer can give: into another account, where the money
           * is still in the book, or out of it, where it reduces the capital
           * paid in. So the fall is carried to the screen as a fact with a
           * question attached, and nothing is written on its behalf. */
        const before = now.settings.mexcLastFree === undefined ? undefined : {
          free: now.settings.mexcLastFree,
          margin: now.settings.mexcLastMargin ?? 0,
        };
        const drop = plan.reading ? outflowBetween(before, plan.reading) : null;

        await saveSettings({
          mexcSyncedAt: new Date().toISOString(),
          mexcError: '',
          mexcGone: plan.goneFromExchange.map((p) => p.symbol),
          ...(plan.reading ? {
            mexcLastFree: plan.reading.free,
            mexcLastMargin: plan.reading.margin,
          } : {}),
          ...(drop ? {
            mexcUnaccounted: (now.settings.mexcUnaccounted ?? 0) + drop.unexplained,
            mexcUnaccountedAt: new Date().toISOString(),
          } : {}),
        });
      } catch (e) {
        const why = e instanceof MexcRefused
          ? `MEXC refused it (${e.code}): ${e.message}`
          : e instanceof Error ? e.message : 'unknown error';
        await saveSettings({ mexcError: why });
      } finally {
        await readSpot();
        running.current = false;
      }
    };

    void tick();
    const timer = window.setInterval(() => void tick(), 60_000);
    const onVisible = () => { if (document.visibilityState === 'visible') void tick(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
    // The book is read through `latest` rather than captured, so it is not a
    // dependency: re-arming this timer on every price tick would mean it never
    // reached five minutes and never fired at all.
  }, [ready, state.ready]);

  return null;
}
